import { Box, Button, IconButton, Text, VStack } from '@chakra-ui/react';
import { useMeetingState } from '@daily-co/daily-react';
import { useProviderCallContext } from '@web-app/context/ProviderCallContext';
import { FC, useEffect, useState } from 'react';
import { BiBug } from 'react-icons/bi';

/**
 * Debug tools for provider call state inspection.
 *
 * Gets state from:
 * - ProviderCallContext (appointment, duration, time limit state)
 * - Daily hooks (meeting state via useMeetingState)
 * - Internal state (liveDurationSeconds - tracked locally for debug display)
 *
 * All state is sourced internally - no props needed!
 *
 * @example
 * ```typescript
 * <DebugTools />
 * ```
 */
export const DebugTools: FC = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [liveDurationSeconds, setLiveDurationSeconds] = useState(0);

  // Get meeting state from Daily hook
  const meetingState = useMeetingState();

  // Get all state from context - organized by domain
  const { lifecycle, duration, ui } = useProviderCallContext();

  // Destructure for cleaner code
  const {
    participantCount,
    isCreatingAppointment,
    isAppointmentCreated,
    hasStartTime,
    momentBothParticipantsJoined,
  } = lifecycle;

  const {
    calculatedDuration,
    editedDuration,
    editReason,
    finalDuration,
    calculateDuration,
    editDuration,
  } = duration;

  const { timeLimitShown, showTimeLimitModal, dismissTimeLimitModal } = ui;

  // Live duration ticker (updates every second for debug display)
  useEffect(() => {
    if (!momentBothParticipantsJoined) {
      setLiveDurationSeconds(0);
      return;
    }

    const updateLiveDuration = () => {
      const now = Date.now();
      const elapsedMs = now - momentBothParticipantsJoined.getTime();
      const elapsedSeconds = Math.floor(elapsedMs / 1000);
      setLiveDurationSeconds(elapsedSeconds);
    };

    // Update immediately
    updateLiveDuration();

    // Then update every second
    const interval = setInterval(updateLiveDuration, 1000);

    return () => clearInterval(interval);
  }, [momentBothParticipantsJoined]);

  return (
    <>
      {/* Bug Icon Toggle Button */}
      <IconButton
        aria-label="Toggle debug tools"
        icon={<BiBug />}
        position="absolute"
        top={4}
        right={4}
        colorScheme={isExpanded ? 'red' : undefined}
        size="lg"
        boxShadow="lg"
        onClick={() => setIsExpanded(!isExpanded)}
        zIndex={10}
      />

      {/* Debug Panel - Only shown when expanded */}
      {isExpanded && (
        <VStack align="stretch" spacing={3}>
          <Text fontSize="lg" fontWeight="bold">
            Duration Tracking Debug
          </Text>

          <Box bg="surface.light" p={3} borderRadius="md">
            <Text
              fontSize="sm"
              fontWeight="semibold"
              mb={2}
              color="text.default"
            >
              Call State
            </Text>
            <Text fontSize="xs" color="text.light">
              Meeting State: <strong>{meetingState || 'Not set'}</strong>
            </Text>
            <Text fontSize="xs" color="text.light">
              Show End Button:{' '}
              {meetingState === 'joined-meeting' ? 'Yes' : 'No'}
            </Text>
          </Box>

          <Box bg="surface.light" p={3} borderRadius="md">
            <Text
              fontSize="sm"
              fontWeight="semibold"
              mb={2}
              color="text.default"
            >
              Appointment
            </Text>
            <Text fontSize="xs" color="text.light">
              Participant Count: {participantCount}
            </Text>
            <Text fontSize="xs" color="text.light">
              Creating: {isCreatingAppointment ? 'Yes' : 'No'}
            </Text>
            <Text fontSize="xs" color="text.light">
              Created: {isAppointmentCreated ? 'Yes' : 'No'}
            </Text>
          </Box>

          <Box bg="surface.light" p={3} borderRadius="md">
            <Text
              fontSize="sm"
              fontWeight="semibold"
              mb={2}
              color="text.default"
            >
              Duration
            </Text>
            <Text fontSize="xs" color="text.light">
              Has Start Time: {hasStartTime ? 'Yes' : 'No'}
            </Text>
            <Text fontSize="xs" color="text.light">
              Start Time:{' '}
              {momentBothParticipantsJoined?.toISOString() || 'Not set'}
            </Text>
            <Text fontSize="md" color="blue.300" fontWeight="bold" my={2}>
              Live: {liveDurationSeconds}s (
              {Math.floor(liveDurationSeconds / 60)}m {liveDurationSeconds % 60}
              s)
            </Text>
            <Text fontSize="xs" color="text.light">
              Calculated: {calculatedDuration} minutes
            </Text>
            <Text fontSize="xs" color="text.light">
              Edited: {editedDuration ?? 'Not edited'} minutes
            </Text>
            {editReason && (
              <Text fontSize="xs" color="text.muted" fontStyle="italic">
                Reason: {editReason}
              </Text>
            )}
            <Text fontSize="xs" color="text.light" fontWeight="bold">
              Final Duration: {finalDuration} minutes
            </Text>
          </Box>

          <Box bg="surface.light" p={3} borderRadius="md">
            <Text
              fontSize="sm"
              fontWeight="semibold"
              mb={2}
              color="text.default"
            >
              Time Limit
            </Text>
            <Text fontSize="xs" color="text.light">
              Ever Shown: {timeLimitShown ? 'Yes' : 'No'}
            </Text>
            <Text fontSize="xs" color="text.light">
              Currently Visible: {showTimeLimitModal ? 'Yes' : 'No'}
            </Text>
          </Box>

          {/* Test buttons to trigger actions */}
          <VStack spacing={2} mt={4}>
            <Button
              size="sm"
              colorScheme="blue"
              onClick={calculateDuration}
              w="full"
            >
              Calculate Duration Now
            </Button>
            <Button
              size="sm"
              colorScheme="orange"
              onClick={() => editDuration(45, 'Technical issue')}
              w="full"
            >
              Edit Duration (45 min)
            </Button>
            {showTimeLimitModal && (
              <Button
                size="sm"
                colorScheme="green"
                onClick={dismissTimeLimitModal}
                w="full"
              >
                Dismiss Time Limit Modal
              </Button>
            )}
          </VStack>
        </VStack>
      )}
    </>
  );
};
